<template>
  <div class="row m3 p-3">
    <h1>Expendeduría</h1>

    <div class="incident-form-container">  
      <div class="dialog-content">
        <!-- MENSAJE DE FEEDBACK (Éxito o Error) -->
        <div 
          v-if="feedbackMessage.text" 
          :class="['alert', feedbackMessage.type === 'success' ? 'alert-success' : 'alert-danger']"
        >
          {{ feedbackMessage.text }}
        </div>

        <form>
          <div class="form-group mb-3">
            <label class="form-label">Expendeduría:</label>
            <search-select
              :items="expendeduriasResults"
              :loading="loadingExpendedurias"
              item-text="name"
              @search="handleExpendeduriaSearch"
              @select="selectExpendeduria"
              placeholder="Buscar expendeduría..."
            />
          </div>
          <!-- IMPRIMIR PDF -->
          <div class="form-actions">
            <button 
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('pdf')"
            >
              <span v-if="isSubmitting">Generando el PDF ...</span>
              <span v-else>Crear PDF</span>
            </button>
          </div>
          <!-- SOLO CREDENCIALES -->
          <div class="form-actions">
            <button
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('credenciales')"
            >
              <span v-if="submittingAction === 'credenciales'">Enviando el correo ...</span>
              <span v-else>Enviar Correo Credenciales</span>
              <span></span>
            </button>
          </div>
          <!-- SOLO CREDENCIALES CC GEMA -->
          <div class="form-actions">
            <button
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('credenciales-gema')"
            >
              <span v-if="submittingAction === 'credenciales-gema'">Enviando el correo ...</span>
              <span v-else>Enviar Correo Credenciales (Copia a Gema)</span>
              <span></span>
            </button>
          </div>
          <!-- CREDENCIALES + SEPA -->
          <div class="form-actions">
            <button
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('credenciales-sepa')"
            >
              <span v-if="submittingAction === 'credenciales-sepa'">Enviando el correo ...</span>
              <span v-else>Credenciales + SEPA</span>
              <span></span>
            </button>
          </div>
          <!-- SEPA RECORDATORIO -->
          <div class="form-actions">
            <button
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('recordatorio-sepa')"
            >
              <span v-if="submittingAction === 'recordatorio-sepa'">Enviando el correo ...</span>
              <span v-else>Enviar Correo Mandato SEPA</span>
              <span></span>
            </button>
          </div>
          <!-- SEPA FIN MES DE PRUEBA -->
          <div class="form-actions">
            <button
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('fin-mes-de-prueba')"
            >
              <span v-if="submittingAction === 'fin-mes-de-prueba'">Enviando el correo ...</span>
              <span v-else>Enviar Correo Fin Mes de Prueba</span>
              <span></span>
            </button>
          </div>
          <!-- SEPA 3 DIAS PRORROGA -->
          <div class="form-actions">
            <button
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('3-dias-prorroga')"
            >
              <span v-if="submittingAction === '3-dias-prorroga'">Enviando el correo ...</span>
              <span v-else>Enviar Correo 3 Días Prórroga</span>
              <span></span>
            </button>
          </div>
          <!-- SEPA ACCESO CADUCADO -->
          <div class="form-actions">
            <button
              type="button"
              :disabled="isSubmitting"
              class="btn btn-dark"
              @click="submitForm('acceso-caducado')"
            >
              <span v-if="submittingAction === 'acceso-caducado'">Enviando el correo ...</span>
              <span v-else>Enviar Correo Acceso Caducado</span>
              <span></span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

  <script setup lang="ts">
  import { ref, reactive, computed } from 'vue';
  import SearchSelect from '@/components/incidents/SearchSelect.vue';
  import entitiesAPI from '@/api/entitiesAPI';
  import utilsApi from '@/api/utilsApi';
  import { FormAction } from '@/types/emailTypes';
  import _ from 'lodash';

  const formData = reactive({
      id_expendeduria: null as number | null,
    });

  const selectExpendeduria = (item: any) => {
      formData.id_expendeduria = item.id;
    };

  const expendeduriasResults = ref<any[]>([]);
  const loadingExpendedurias = ref(false);

  const submittingPdf = ref(false);
  const submittingAction = ref<FormAction | null>(null);

  const isSubmitting = computed(() => submittingPdf.value || submittingAction.value !== null);

  const feedbackMessage = reactive({
    text: '',
    type: 'success' as 'success' | 'error',
  });

  const showNotification = (text: string, type: 'success' | 'error') => {
    feedbackMessage.text = text;
    feedbackMessage.type = type;
    setTimeout(() => {
      feedbackMessage.text = '';
    }, 4000);
  };
  
  const handleExpendeduriaSearch = _.debounce(async (query: string) => {
    if (query === "") {
      expendeduriasResults.value = [];
      return
    }
    
    try {
      loadingExpendedurias.value = true;
      const response = await entitiesAPI.searchExpendeduria(query)
      expendeduriasResults.value = response.results.slice(0, 10);
    } catch (error) {
      console.error('Error buscando expendedurías:', error);
    } finally {
      loadingExpendedurias.value = false;
    }
  }, 300);
  
  
  // Envío del formulario
  const submitForm = async (action: FormAction) => {    
    if (!formData.id_expendeduria) {
      showNotification('Por favor, selecciona una expendeduría antes de enviar el formulario.', 'error');
      return;
    }
    
    try {
      if (action === 'pdf') {
        submittingPdf.value = true;
        await utilsApi.getPDF(formData.id_expendeduria);
        showNotification('PDF generado correctamente.', 'success');
      } else {
        submittingAction.value = action;
        await utilsApi.sendEmail(formData.id_expendeduria, action);
        showNotification('Correo enviado correctamente.', 'success');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('Ocurrió un error al procesar la solicitud.', 'error');
    } finally {
      submittingPdf.value = false;
      submittingAction.value = null;
    }
  };

  </script>

<style scoped>
.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-weight: bold;
  text-align: center;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.form-actions {
  margin-bottom: 10px;
}
</style>
